import { useQuery } from '@tanstack/react-query';
import { Tabs, type TabItem } from '@/components/ui/tabs';
import { usePermissions } from '@/lib/me';
import { apartmentChannelsQuery } from '@/lib/channels';
import type { Apartment } from '@/lib/apartments';
import { OverviewSection } from '@/components/apartment/OverviewSection';
import { ApartmentContent } from '@/components/ApartmentContent';
import { ApartmentPhotos } from '@/components/ApartmentPhotos';
import { ApartmentPricing } from '@/components/ApartmentPricing';
import { ApartmentChannels } from '@/components/ApartmentChannels';
import { AccessOpsSection } from '@/components/apartment/AccessOpsSection';
import { ApartmentFinanceSection } from '@/components/apartment/ApartmentFinanceSection';
import { DebugSection } from '@/components/apartment/DebugSection';

export type ApartmentTab =
  | 'overview' | 'content' | 'photos' | 'pricing' | 'channels' | 'access' | 'finance' | 'debug';

// read/write actions per tab — the same actions the backend authorizes.
export const TAB_PERMISSIONS: Record<ApartmentTab, { read: string; write: string | null }> = {
  overview: { read: 'apartments.read', write: 'apartments.write' },
  content:  { read: 'apartments.read', write: 'apartments.write' },
  photos:   { read: 'apartments.read', write: 'apartments.write' },
  pricing:  { read: 'pricing.read',    write: 'pricing.write' },
  channels: { read: 'apartments.read', write: 'apartments.write' },
  access:   { read: 'apartments.read', write: 'apartments.write' },
  // Writes (rules, manual expected payments) happen on the finance.rules.*
  // and finance.expected.* screens this tab links into, not here — this
  // tab itself is read-only, same as debug.
  finance:  { read: 'finance.read',    write: null },
  debug:    { read: 'debug.read',      write: null },
};

const TAB_LABELS: Record<ApartmentTab, string> = {
  overview: 'Overview', content: 'Content', photos: 'Photos', pricing: 'Pricing',
  channels: 'Channels', access: 'Access & ops', finance: 'Finance', debug: 'Debug',
};

const TAB_ORDER: ApartmentTab[] = ['overview', 'content', 'photos', 'pricing', 'channels', 'access', 'finance', 'debug'];

export function visibleTabs(can: (p: string) => boolean): ApartmentTab[] {
  return TAB_ORDER.filter((t) => can(TAB_PERMISSIONS[t].read));
}

export function ApartmentDetail({
  apt, tab, onTabChange,
}: { apt: Apartment; tab: ApartmentTab; onTabChange: (t: ApartmentTab) => void }) {
  const { can } = usePermissions();
  const allowed = visibleTabs(can);
  const active = allowed.includes(tab) ? tab : (allowed[0] ?? 'overview');
  // Read at shell level (not just inside ApartmentChannels) so the Channels
  // tab's badge is populated before the operator ever opens it — Task 11.
  // active_error_total is a required field on this response (unlike
  // active_error_count, it's never omitted), but the query itself can still
  // be loading/erroring at this point, hence the optional chaining.
  const { data: channels } = useQuery(apartmentChannelsQuery(apt.id));
  const items: TabItem[] = allowed.map((t) => ({
    value: t,
    label: TAB_LABELS[t],
    count: t === 'channels' ? channels?.active_error_total || undefined : undefined,
  }));
  const canWrite = (t: ApartmentTab) => {
    const w = TAB_PERMISSIONS[t].write;
    return w !== null && can(w);
  };

  return (
    <div className="space-y-5">
      <Tabs tabs={items} value={active} onChange={(v) => onTabChange(v as ApartmentTab)} />
      {active === 'overview' && <OverviewSection apt={apt} canWrite={canWrite('overview')} />}
      {active === 'content' && (
        <ApartmentContent apartmentID={apt.id} apt={apt} canWrite={canWrite('content')} />
      )}
      {active === 'photos' && (
        <ApartmentPhotos apartmentID={apt.id} canWrite={canWrite('photos')} />
      )}
      {active === 'pricing' && (
        <ApartmentPricing apartmentID={apt.id} canWrite={canWrite('pricing')} />
      )}
      {active === 'channels' && (
        <ApartmentChannels apartmentID={apt.id} canWrite={canWrite('channels')} />
      )}
      {active === 'access' && <AccessOpsSection apartmentID={apt.id} canWrite={canWrite('access')} />}
      {active === 'finance' && <ApartmentFinanceSection apartmentID={apt.id} />}
      {active === 'debug' && <DebugSection apartmentID={apt.id} />}
    </div>
  );
}
